import {
  exportInvoiceItems,
  exportInvoiceItemsVariables,
  invoiceItems,
  invoiceItemsVariables,
  pendingInvoiceItems,
  pendingInvoiceItemsVariables,
  updateInvoiceItem,
  updateInvoiceItemVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import { gql } from "graphql-tag";

export const InvoiceItemPayload = gql`
  fragment InvoiceItemPayload on InvoiceItem {
    id
    addon {
      id
      title
      price
    }
    amount
    balanceAccountId
    billingCategory {
      id
      title
    }
    charge {
      id
      amount
      billingCategory {
        id
        title
      }
      createdBy {
        id
        firstName
        lastName
      }
      description
    }
    chargeAmount {
      id
    }
    coupons {
      id
      amount
      coupon {
        id
        code
        title
        department {
          id
        }
      }
    }
    createdAt
    credits {
      id
      amount
      credit {
        id
        description
        billingCategory {
          id
          title
        }
      }
    }
    description
    discounts {
      id
      amount
      description
      discount {
        id
        discountType
        department {
          id
        }
      }
    }
    events {
      id
      createdAt
      type
      by {
        id
        firstName
        lastName
      }
    }
    fee {
      id
      title
      amount
    }
    groupRegistration {
      id
      accountOwner {
        id
        firstName
        lastName
      }
      class {
        id
        additionalFeeAmount
        additionalFeeTitle
        chargeAmounts(
          where: {
            OR: [{ status: { not: Processed } }, { atRegistration: true }]
          }
        ) {
          id
          atRegistration
          date
          membershipOffering {
            id
            title
          }
          price
        }
        classTimes {
          id
          endTime
          day
          location {
            id
            title
          }
          startTime
        }
        collectTaxes
        department {
          id
          title
        }
        endDate
        fees {
          id
        }
        location {
          id
          title
        }
        price
        minAge
        maxAge
        participantType
        paymentFrequency
        paymentRate
        program {
          id
          title
          department {
            id
          }
          billingCategory {
            id
            title
          }
        }
        prorateInProgressClasses
        prorateStartEnd
        startDate
        status
        tags {
          id
          title
        }
        title
        type
      }
      events {
        id
        type
        by {
          id
          firstName
          lastName
          level
        }
      }
      roster {
        id
        firstName
        lastName
      }
      status
      student {
        id
        firstName
        lastName
      }
    }
    invoice {
      id
      invoiceDate
      status
    }
    meet {
      id
      city
      description
      endDate
      startDate
      state
      status
      title
    }
    membership {
      id
      events {
        id
        type
        value
      }
      offering {
        id
        paymentFrequency
        price
        priceLevels {
          id
          price
        }
        program {
          id
          title
        }
        title
        type
      }
      roster {
        id
        firstName
        lastName
      }
      status
    }
    note
    payments {
      createdAt
      id
      amount
      payment {
        id
        amount
        checkNumber
        createdAt
        createdBy {
          id
          firstName
          lastName
        }
        customerServiceItems {
          id
          type
          createdAt
          additionalData
        }
        description
        interchange
        scheme
        markup
        stripeFeeAmount
        stripeChargeId
        stripeFee
        stripeToken
        stripe
        type
      }
    }
    privateSerie {
      id
      accountOwner {
        id
        firstName
        lastName
      }
      attendances {
        id
        status
      }
      events {
        id
        type
        value
      }
      instructor {
        id
        firstName
        lastName
      }
      numberOfLessons
      offering {
        id
        billingCategory {
          id
          title
        }
        collectTaxes
        department {
          id
          title
        }
        fees {
          id
        }
        price
        title
        type
      }
      roster {
        id
        firstName
        lastName
      }
    }
    quantity
    recurringCharge {
      id
      amount
      billingCategory {
        id
        title
      }
    }
    refunds {
      id
      amount
      createdAt
      createdBy {
        id
        firstName
        lastName
      }
      description
      stripeRefundId
      type
    }
    status
  }
`;

const PendingInvoiceItemPayloadFragment = gql`
  fragment PendingInvoiceItemPayload on InvoiceItem {
    amount
    addon {
      id
      title
      price
      department {
        id
        title
      }
    }
    createdAt
    description
    events {
      id
      type
      value
      by {
        id
        firstName
        lastName
      }
    }
    id
    invoice {
      id
      status
      user {
        id
        firstName
        lastName
      }
    }
    payments {
      id
      amount
      payment {
        id
        type
      }
    }
    credits {
      id
      amount
    }
    quantity
    note
    status
  }
`;

export const UpdateInvoiceItemMutation = gql`
  mutation updateInvoiceItem(
    $data: InvoiceItemCreateInput!
    $where: UniqueInput!
  ) {
    updateInvoiceItem(data: $data, where: $where) {
      ...InvoiceItemPayload
    }
  }
  ${InvoiceItemPayload}
` as TypedDocumentNode<updateInvoiceItem, updateInvoiceItemVariables>;

export const PendingInvoiceItemQuery = gql`
  query pendingInvoiceItems($organizationId: String!) {
    invoiceItems(
      skipCount: true
      where: {
        addonId: { not: null }
        status: { equals: Pending }
        invoice: {
          organizationId: $organizationId
          status: { notIn: [Preview, Error] }
        }
      }
    ) {
      ...PendingInvoiceItemPayload
    }
  }
  ${PendingInvoiceItemPayloadFragment}
` as TypedDocumentNode<pendingInvoiceItems, pendingInvoiceItemsVariables>;

export const InvoiceItemsQueryFragment = gql`
  fragment InvoiceItemsQueryFragment on InvoiceItem {
    id
    addon {
      id
    }
    amount
    charge {
      id
    }
    createdAt
    credits {
      id
      amount
    }
    description
    fee {
      id
      amount
    }
    groupRegistration {
      id
      class {
        paymentFrequency
      }
    }
    invoice {
      id
      automated
      createdAt
      user {
        id
        firstName
        lastName
      }
      organization {
        title
        subdomain
      }
    }
    meet {
      id
    }
    membership {
      id
      offering {
        paymentFrequency
      }
    }
    payments {
      id
      amount
      payment {
        id
        type
        checkNumber
        amount
        customerServiceItems {
          id
          type
          value
          createdAt
        }
        createdAt
        description
        stripeFee
        stripeToken
        stripeChargeId
        stripe
        interchange
        scheme
        markup
      }
    }
    recurringCharge {
      id
    }
    refunds {
      id
      amount
      createdAt
      type
      description
    }
  }
`;

export const InvoiceItemsQuery = gql`
  query invoiceItems(
    $where: InvoiceItemWhereInput
    $orderBy: DefaultOrderByInput
    $take: Int
    $skip: Int
    $filter: String
    $reports: Boolean
  ) {
    invoiceItems(
      where: $where
      orderBy: $orderBy
      take: $take
      skip: $skip
      filter: $filter
      reports: $reports
    ) {
      ...InvoiceItemsQueryFragment
    }
  }
  ${InvoiceItemsQueryFragment}
` as TypedDocumentNode<invoiceItems, invoiceItemsVariables>;

export const ExportInvoiceItemsMutation = gql`
  mutation exportInvoiceItems($where: InvoiceItemWhereInput, $filter: String) {
    exportInvoiceItems(where: $where, filter: $filter)
  }
` as TypedDocumentNode<exportInvoiceItems, exportInvoiceItemsVariables>;
